import {
  ArrowRight,
  CheckCircle2,
  Gift,
  Heart,
  ImagePlus,
  Magnet,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";

import SEO from "../../components/SEO";
import StructuredData from "../../components/StructuredData";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { SITE_URL } from "../../config/site";

import "./ImanesLanding.css";

const PACKS = [
  { name: "Pack 2", price: "6,50 €", text: "Para probar, completar un regalo o preparar un detalle sencillo." },
  { name: "Pack 4", price: "12,00 €", text: "El formato más cómodo para regalar varias fotos favoritas." },
  { name: "Pack 8", price: "24,00 €", text: "Ideal para colecciones familiares, viajes, mascotas o pareja." },
  { name: "Pack 12", price: "32,00 €", text: "Más recuerdos con mejor precio por unidad." },
];

const IDEAS = [
  "Fotos familiares",
  "Mascotas",
  "Viajes",
  "Pareja",
  "Abuelos",
  "Momentos especiales",
];

const MOMENT_GALLERY = [
  {
    className: "imanes-moment-image--main",
    src: "/images/frame-gallery/frame-gallery-imanes/iman-personalizado-escursion.webp",
    alt: "Colección de imanes personalizados creada con fotografías diferentes",
  },
  {
    className: "imanes-moment-image--portrait",
    src: "/images/frame-gallery/frame-gallery-imanes/iman-personalizado-familia.webp",
    alt: "Imán personalizado con una fotografía familiar",
  },
];

const EVENT_USES = [
  "Recuerdos para invitados",
  "Bodas y aniversarios",
  "Comuniones y bautizos",
  "Cumpleaños y celebraciones",
];

const CORPORATE_USES = [
  "Regalos para clientes",
  "Ferias y congresos",
  "Campañas promocionales",
  "Asociaciones y equipos",
];

const RELATED = [
  {
    title: "Collage magnético",
    text: "Convierte una foto especial en varias piezas imantadas.",
    link: "/editor",
    image: "/images/frame-gallery/frame-gallery-collage/frame-gallery-collage.3.webp",
    imageAlt: "Collage magnético personalizado con una fotografía",
  },
  {
    title: "Marcos para imanes",
    text: "Prueba modelos y colores para exponer tus imanes personalizados en superficies metálicas o con soporte.",
    link: "/marcos-individuales",
    image: "/images/frame-gallery/frame-gallery-individuales/frame-gallery-individuales-nevera.webp",
    imageAlt: "Marcos de colores para imanes personalizados",
  },
  {
    title: "Soporte para marcos",
    text: "Coloca tus marcos con imanes en estanterías, muebles o escritorio sin depender de la nevera.",
    link: "/producto/soporte-marcos-imanes",
    image: "/images/frame-gallery/frame-gallery-soportes/frame-gallery-soportes-reverso copia.webp",
    imageAlt: "Soportes de colores para colocar marcos con imanes",
  },
  {
    title: "Noria expositora",
    text: "Una forma original de presentar tus fotos imantadas.",
    link: "/producto/noria-imanes",
    image: "/images/frame-gallery/frame-gallery-noria/frame-gallery-noria-imanes.webp",
    imageAlt: "Noria expositora con imanes personalizados",
  },
  {
    title: "Portalápices para imanes",
    text: "Un accesorio de escritorio para tener tus imanes personalizados siempre a la vista.",
    link: "/producto/portalapices-imanes",
    image: "/images/frame-gallery/frame-gallery-portalapices/frame-gallery-soportes-escritorio-mesa.webp",
    imageAlt: "Portalápices para mostrar imanes personalizados en el escritorio",
  },
];

const PHOTO_MOSAIC = [
  {
    src: "/images/frame-gallery/frame-gallery-imanes/iman-coleccion-todos.png",
    alt: "Colección variada de imanes personalizados con fotos",
  },
  {
    src: "/images/frame-gallery/frame-gallery-imanes/iman-boda-reverso.png",
    alt: "Reverso de un imán personalizado para boda",
  },
  {
    src: "/images/frame-gallery/frame-gallery-imanes/iman-coleccion-infantil.png",
    alt: "Colección de imanes personalizados con dibujos infantiles",
  },
  {
    src: "/images/frame-gallery/frame-gallery-imanes/iman-coleccion-mama.png",
    alt: "Colección de imanes personalizados para mamá",
  },
  {
    src: "/images/frame-gallery/frame-gallery-imanes/iman-coleccion-salto.png",
    alt: "Colección de imanes personalizados con una fotografía de salto",
  },
];

const FAQS = [
  {
    question: "¿Qué tamaño tienen los imanes personalizados?",
    answer:
      "Nuestros imanes personalizados principales tienen un tamaño fijo de 5x5 cm. Es una medida pequeña, cuadrada y muy cómoda para crear colecciones.",
  },
  {
    question: "¿Sirven para la nevera?",
    answer:
      "Sí. Están pensados para colocarse en neveras y otras superficies metálicas. También puedes combinarlos con nuestros marcos y con el soporte opcional para colocarlos sobre muebles, estanterías o escritorios.",
  },
  {
    question: "¿Son solo fotos impresas sobre papel imantado?",
    answer:
      "No. Los fabricamos nosotros de forma artesanal y añadimos una capa protectora sobre la imagen para darles un acabado más cuidado.",
  },
  {
    question: "¿Puedo subir fotos hechas con el móvil?",
    answer:
      "Sí. La mayoría de fotos de móvil funcionan bien. Si alguna imagen no permite un buen resultado, podemos contactar contigo antes de producir el pedido.",
  },
  {
    question: "¿Qué tipo de fotos quedan mejor?",
    answer:
      "Funcionan muy bien primeros planos, mascotas, niños, viajes, fotos familiares y recuerdos con buena luz. En el editor puedes revisar el encuadre antes de añadir al carrito.",
  },
  {
    question: "¿Cuánto tarda la producción?",
    answer:
      "La producción suele tardar entre 2 y 5 días laborables desde la confirmación del pago. El plazo puede variar según el producto, la cantidad y la carga de pedidos.",
  },
  {
    question: "¿Cuánto cuesta el envío?",
    answer:
      "Los pedidos para España peninsular tienen una tarifa plana de 4,95 €, que se muestra en el carrito antes de continuar al pago.",
  },
  {
    question: "¿Puedo pedir imanes personalizados para una boda o celebración?",
    answer:
      "Sí. Preparamos imanes para bodas, comuniones, bautizos, aniversarios, cumpleaños y otras celebraciones. Puedes consultarnos la cantidad, las fotografías y la fecha antes de preparar el pedido.",
  },
  {
    question: "¿Preparáis imanes corporativos personalizados con logotipo?",
    answer:
      "Sí. Podemos convertir un logotipo o diseño de marca en imanes para clientes, ferias, campañas promocionales, asociaciones y equipos. Contacta con nosotros para valorar la cantidad y los detalles del proyecto.",
  },
];

export default function ImanesLanding() {
  const canonical = `${SITE_URL}/imanes-personalizados-con-fotos`;
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE_URL}/` },
        {
          "@type": "ListItem",
          position: 2,
          name: "Imanes personalizados con fotos",
          item: canonical,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Imanes personalizados con fotos 5x5 cm",
      description:
        "Fotoimanes artesanales de 5x5 cm con capa protectora, disponibles en packs.",
      image: `${SITE_URL}/images/frame-gallery/frame-gallery-imanes/iman-personalizado-colecciones-slider.webp`,
      brand: { "@type": "Brand", name: "Imalandia" },
      offers: PACKS.map((pack) => ({
        "@type": "Offer",
        url: `${SITE_URL}/fotos`,
        priceCurrency: "EUR",
        price: pack.price.replace(" €", "").replace(",", "."),
        availability: "https://schema.org/InStock",
        name: pack.name,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  ];

  return (
    <div className="imanes-landing-page">
      <SEO
        title="Imanes personalizados con fotos | Imalandia"
        description="Crea imanes personalizados con tus fotos favoritas. Imanes artesanales de 5x5 cm con capa protectora, pensados para nevera, regalos y colecciones."
        canonical={canonical}
        image="/images/frame-gallery/frame-gallery-imanes/iman-personalizado-colecciones-slider.webp"
      />
      <StructuredData data={structuredData} />

      <Header />

      <main>
        <section className="imanes-hero">
          <div className="imanes-hero-content">
            <p>Tus recuerdos, siempre a la vista</p>
            <h1>Imanes personalizados con tus fotos</h1>
            <span>
              Convierte los momentos que guardas en el móvil en fotoimanes
              artesanales de 5x5 cm, protegidos y preparados con cuidado para
              regalar, coleccionar o disfrutar cada día.
            </span>

            <div className="imanes-hero-trust">
              <strong>
                <Magnet aria-hidden="true" />
                Para nevera
              </strong>
              <strong>
                <ShieldCheck aria-hidden="true" />
                Capa protectora
              </strong>
              <strong>
                <Sparkles aria-hidden="true" />
                Hechos por nosotros
              </strong>
            </div>

            <div className="imanes-hero-actions">
              <Link to="/fotos">
                Crear mis imanes
                <ArrowRight aria-hidden="true" />
              </Link>

              <a href="#packs">Ver packs y precios</a>
            </div>
          </div>

          <div className="imanes-hero-media">
            <video
              src="/videos/final-web.mp4"
              poster="/images/frame-gallery/frame-landing/frame-landing-poster.webp"
              muted
              loop
              autoPlay
              playsInline
              preload="metadata"
              aria-label="Video de imanes personalizados con fotos"
            />
          </div>
        </section>

        <section
          className="imanes-moment-showcase"
          aria-labelledby="imanes-moment-title"
        >
          <div className="imanes-moment-content">
            <p>Ideas con tus fotos</p>
            <h2 id="imanes-moment-title">
              Momentos que merecen salir del móvil
            </h2>
            <span>
              Reúne escenas cotidianas, viajes, mascotas y personas importantes
              en una colección personal que puedes ver y disfrutar cada día.
              Cada foto se convierte en una pieza diferente, pero todas cuentan
              una historia en común.
            </span>

            <div className="imanes-moment-tags" aria-label="Ideas para personalizar">
              {IDEAS.map((idea) => (
                <strong key={idea}>{idea}</strong>
              ))}
            </div>
          </div>

          <div className="imanes-moment-collage">
            {MOMENT_GALLERY.map((image) => (
              <figure
                key={image.src}
                className={`imanes-moment-image ${image.className}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  decoding="async"
                />
              </figure>
            ))}
          </div>
        </section>

        <section
          className="imanes-section imanes-corporate imanes-events"
          aria-labelledby="imanes-events-title"
        >
          <div className="imanes-corporate-media imanes-events-media">
            <img
              className="imanes-product-image--shadow"
              src="/images/frame-gallery/frame-gallery-imanes/iman-boda.png"
              alt="Imanes personalizados preparados como recuerdos para una boda"
              loading="lazy"
              decoding="async"
            />
            <img
              className="imanes-product-image--shadow"
              src="/images/frame-gallery/frame-gallery-imanes/iman-comunion.png"
              alt="Imán personalizado para una celebración familiar"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="imanes-corporate-content imanes-events-content">
            <p>Bodas y celebraciones</p>
            <h2 id="imanes-events-title">
              Imanes personalizados para bodas y celebraciones
            </h2>
            <span>
              Convierte una fotografía, un nombre o una fecha especial en un
              recuerdo que los invitados puedan llevarse y conservar. Preparamos
              imanes para bodas, comuniones, bautizos, aniversarios, cumpleaños
              y otras celebraciones familiares.
            </span>

            <div className="imanes-corporate-tags imanes-events-tags">
              {EVENT_USES.map((use) => (
                <strong key={use}>
                  <CheckCircle2 aria-hidden="true" />
                  {use}
                </strong>
              ))}
            </div>

            <div className="imanes-corporate-actions imanes-events-actions">
              <a
                href="https://wa.me/34646273415?text=Hola%2C%20quiero%20informaci%C3%B3n%20sobre%20imanes%20personalizados%20para%20una%20boda%20o%20celebraci%C3%B3n"
                target="_blank"
                rel="noreferrer"
              >
                Consultar imanes para mi evento
                <ArrowRight aria-hidden="true" />
              </a>

              <Link to="/contacto">
                Contar los detalles
                <ArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        <section
          className="imanes-section imanes-corporate"
          aria-labelledby="imanes-corporate-title"
        >
          <div className="imanes-corporate-content">
            <p>Empresas y promociones</p>
            <h2 id="imanes-corporate-title">
              Imanes corporativos personalizados para empresas
            </h2>
            <span>
              Convierte tu logotipo, campaña o diseño de marca en un soporte
              útil para clientes, ferias y acciones promocionales. Adaptamos la
              preparación y la cantidad a las necesidades de cada negocio,
              asociación o equipo.
            </span>

            <div className="imanes-corporate-tags">
              {CORPORATE_USES.map((use) => (
                <strong key={use}>
                  <CheckCircle2 aria-hidden="true" />
                  {use}
                </strong>
              ))}
            </div>

            <div className="imanes-corporate-actions">
              <a
                href="https://wa.me/34646273415?text=Hola%2C%20quiero%20solicitar%20presupuesto%20para%20imanes%20corporativos%20personalizados"
                target="_blank"
                rel="noreferrer"
              >
                Solicitar presupuesto
                <ArrowRight aria-hidden="true" />
              </a>

              <Link to="/contacto">
                Enviar información
                <ArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="imanes-corporate-media">
            <img
              className="imanes-product-image--shadow"
              src="/images/frame-gallery/frame-gallery-imanes/iman-corporativo-alarcauto.png"
              alt="Imanes corporativos personalizados con el logotipo de una empresa"
              loading="lazy"
              decoding="async"
            />
            <img
              className="imanes-product-image--shadow"
              src="/images/frame-gallery/frame-gallery-imanes/iman-corporativo-espai.png"
              alt="Imanes promocionales personalizados para empresas"
              loading="lazy"
              decoding="async"
            />
          </div>
        </section>

        <section
          className="imanes-section imanes-benefits"
          aria-labelledby="imanes-benefits-title"
        >
          <div className="imanes-section-heading">
            <p>Formato y acabado</p>
            <h2 id="imanes-benefits-title">
              Imanes con foto de 5x5 cm preparados con cuidado
            </h2>
            <span className="imanes-section-intro">
              Un formato compacto para convertir fotografías diferentes en
              colecciones, regalos y recuerdos que puedes colocar sobre
              superficies metálicas.
            </span>
          </div>

          <div className="imanes-benefits-grid imanes-quality-grid">
            <article>
              <Magnet aria-hidden="true" />
              <h3>Formato cuadrado 5x5 cm</h3>
              <p>
                Una medida cómoda para combinar varias fotos en la nevera,
                nuestros marcos y otros soportes magnéticos.
              </p>
            </article>

            <article>
              <ShieldCheck aria-hidden="true" />
              <h3>Capa protectora</h3>
              <p>
                Añadimos una capa sobre la fotografía para proteger su
                superficie y conseguir un acabado uniforme.
              </p>
            </article>

            <article>
              <Heart aria-hidden="true" />
              <h3>Preparados por Imalandia</h3>
              <p>
                Montamos y revisamos cada pieza antes de preparar el pedido para
                que la colección mantenga un resultado coherente.
              </p>
            </article>

            <article>
              <Gift aria-hidden="true" />
              <h3>Packs para cada ocasión</h3>
              <p>
                Elige desde dos unidades para un detalle hasta doce imanes para
                reunir viajes, familia, mascotas o momentos especiales.
              </p>
            </article>
          </div>
        </section>

        <section id="packs" className="imanes-section imanes-packs" aria-labelledby="imanes-packs-title">
          <div className="imanes-section-heading">
            <p>Packs y precios</p>
            <h2 id="imanes-packs-title">
              Packs de imanes personalizados con foto 5x5 cm
            </h2>
            <span className="imanes-section-intro">
              Elige la cantidad que necesitas y utiliza una fotografía distinta
              en cada imán. El precio del pack se muestra antes de comenzar la
              personalización.
            </span>
          </div>

          <div className="imanes-packs-grid">
            {PACKS.map((pack) => (
              <article key={pack.name}>
                <strong>{pack.name}</strong>
                <span>{pack.price}</span>
                <p>{pack.text}</p>
              </article>
            ))}
          </div>

          <div className="imanes-packs-actions">
            <Link className="imanes-section-cta" to="/fotos">
              Elegir pack y subir fotos
              <ImagePlus aria-hidden="true" />
            </Link>
            <Link
              className="imanes-section-cta imanes-offers-cta"
              to="/ofertas"
            >
              Ver ofertas en imanes personalizados
              <Gift aria-hidden="true" />
            </Link>
          </div>
        </section>

        <section
          className="imanes-photo-mosaic"
          aria-label="Ejemplos de imanes personalizados y composiciones con fotos"
        >
          <img
            className="imanes-product-image--shadow"
            src={PHOTO_MOSAIC[0].src}
            alt={PHOTO_MOSAIC[0].alt}
            width="420"
            height="420"
            loading="lazy"
            decoding="async"
          />
          <div className="imanes-photo-mosaic-small">
            {PHOTO_MOSAIC.slice(1).map((image) => (
              <img
                className="imanes-product-image--shadow"
                key={image.src}
                src={image.src}
                alt={image.alt}
                width="206"
                height="206"
                loading="lazy"
                decoding="async"
              />
            ))}
          </div>
        </section>

        <section className="imanes-section imanes-process" aria-labelledby="imanes-process-title">
          <div className="imanes-section-heading">
            <p>Cómo funciona</p>
            <h2 id="imanes-process-title">
              Cómo crear tus imanes personalizados online
            </h2>
            <span className="imanes-section-intro">
              Prepara el pedido desde el móvil o el ordenador y revisa cada
              fotografía antes de añadir el pack al carrito.
            </span>
          </div>

          <div className="imanes-process-grid">
            <article>
              <span>1</span>
              <h3>Sube y encuadra tus fotos</h3>
              <p>
                Elige las imágenes y ajusta el encuadre para decidir qué parte
                aparecerá en cada imán.
              </p>
            </article>

            <article>
              <span>2</span>
              <h3>Elige el pack y revisa</h3>
              <p>
                Comprueba las fotografías, la cantidad y el resumen del carrito
                antes de continuar al pago.
              </p>
            </article>

            <article>
              <span>3</span>
              <h3>Preparamos tu pedido</h3>
              <p>
                Fabricamos, protegemos y revisamos los imanes antes de preparar
                el envío.
              </p>
            </article>
          </div>
        </section>

        <section className="imanes-section imanes-display" aria-labelledby="imanes-display-title">
          <div className="imanes-display-content">
            <p>Nevera, marcos y soportes</p>
            <h2 id="imanes-display-title">
              Cómo colocar y exponer tus imanes personalizados
            </h2>
            <span>
              Colócalos directamente en la nevera o combínalos con nuestros
              marcos para crear composiciones. Con el soporte opcional también
              puedes llevarlos a un mueble, una estantería o el escritorio.
            </span>

            <div className="imanes-display-actions">
              <Link to="/marcos-individuales">
                Ver marcos para imanes
                <ArrowRight aria-hidden="true" />
              </Link>

              <Link to="/producto/soporte-marcos-imanes">
                Ver soporte opcional
                <ArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="imanes-display-media">
            <img
              className="imanes-product-image--shadow"
              src="/images/frame-gallery/frame-gallery-individuales/frame-gallery-individuales-casa-corazon.png"
              alt="Marcos de Imalandia para exponer imanes personalizados"
              loading="lazy"
              decoding="async"
            />
            <img
              className="imanes-product-image--shadow"
              src="/images/frame-gallery/frame-gallery-individuales/frame-gallery-individuales-tim.png"
              alt="Soporte montado para colocar marcos con imanes sobre un mueble"
              loading="lazy"
              decoding="async"
            />
          </div>
        </section>

        <section className="imanes-section imanes-related" aria-labelledby="imanes-related-title">
          <div className="imanes-section-heading">
            <p>Completa tu colección</p>
            <h2 id="imanes-related-title">
              Marcos, soportes y composiciones para tus fotos
            </h2>
            <span className="imanes-section-intro">
              Descubre otras formas de crear con tus imágenes y exponer los
              imanes dentro y fuera de la nevera.
            </span>
          </div>

          <div className="imanes-related-grid">
            {RELATED.map((item) => (
              <Link key={item.link} to={item.link}>
                <img
                  className="imanes-related-image"
                  src={item.image}
                  alt={item.imageAlt}
                  width="112"
                  height="112"
                  loading="lazy"
                  decoding="async"
                />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <span>
                  Ver producto
                  <ArrowRight aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="imanes-section imanes-faq" aria-labelledby="imanes-faq-title">
          <div className="imanes-section-heading">
            <p>Preguntas frecuentes</p>
            <h2 id="imanes-faq-title">
              Preguntas sobre imanes personalizados con fotos
            </h2>
          </div>

          <div className="imanes-faq-list">
            {FAQS.map((item) => (
              <article key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>

          <Link className="imanes-section-cta imanes-final-cta" to="/fotos">
            Crear mis imanes personalizados
            <ArrowRight aria-hidden="true" />
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
